import { Button, Grid, Skeleton } from '@design-systems/canvas-ui'
import {
    TimeDimension,
    useDimensionDetails,
} from 'hooks/operations/useDimensions'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { ReactNode } from 'react'
import Icon, { ErrorIcon } from '@design-systems/react-icons'
import {
    adaptStatusColorToClass,
    adaptStatusColorToIcon,
} from 'utils/style-adapters'
import { SUCCESS_HEX, TOTAL_PRIORITY_FILL, sortTimePeriods } from './helpers'
import { useLogClick } from 'utils/analytics'
import { OperationsMetricsTile } from 'types/api'
import { Cell } from 'utils/data'

/**
 * Hooks up and massages all the data for a given operational metric card.
 * This includes:
 * * parsing all the relevant time periods
 * * calling the correct card with each of those time periods
 * * Pulling data from the relevant cell to show
 *
 * @param metric the "tile" type object from the `/dimensions` call
 * @returns ReactNode
 */
export const OperationsDetails = ({
    metric,
}: {
    metric?: OperationsMetricsTile
}) => {
    const { t, currentLocation, env, session } = useEssentials()
    const { timePeriods, operationalMetrics } = TRANSLATION_KEYS

    const tileData = metric
        ? sortTimePeriods(
              metric.time_period?.split(',').map((t) => t.trim()) ?? [],
          ).map((time) => ({
              ...metric,
              time_dimension: time as TimeDimension,
          }))
        : []

    const responses = useDimensionDetails({
        env,
        accessToken: session?.accessToken ?? '',
        tiles: tileData,
        location: currentLocation,
    })

    if (!metric) {
        return null
    }

    const titles: Record<TimeDimension, string> = {
        'Prior Day': t(timePeriods.yesterday),
        'Week to Day': t(timePeriods.weekToDate),
        'Prior Week': t(timePeriods.lastWeek),
        'Month to Day': t(timePeriods.monthToDate),
        'Prior Month': t(operationalMetrics.timePeriods.priorMonth),
        'Year to Day': t(timePeriods.yearToDate),
    }

    const historicals = responses.map((item, index) => ({
        ...item,
        title: titles[tileData[index].time_dimension],
        tile: item.tile.rows?.[0]?.[1],
    }))
    return (
        <>
            {historicals.map((item, index) => (
                <HistoricalLineItem
                    {...item}
                    key={`${metric.metric_name}-${index}`}
                    id={`${metric.metric_name}-${index}`}
                    metric_name={metric.metric_name}
                />
            ))}
        </>
    )
}

/**
 * Private Component
 *
 * Handles all the loading / error / render view logic related
 * to a single row of the Historical Details within an Operations Card expander.
 *
 * @param props
 * @returns
 */
const HistoricalLineItem = (props: {
    id: string
    isLoading: boolean
    isError: boolean
    title: string
    refetch: Function
    tile?: Cell
    metric_name: string
}) => {
    const { t } = useEssentials()
    const { generalErrors } = TRANSLATION_KEYS
    const { logClick } = useLogClick('operations')
    const className = props.tile?.color
        ? adaptStatusColorToClass(props.tile?.color)
        : ''
    const icon = props.tile?.color
        ? adaptStatusColorToIcon(props.tile?.color)
        : undefined
    // Loading must have _some_ width to render
    // Error shows for isError OR if not loading but no data
    // Success value can have it's own width, no need to enforce a grid sizing
    const render: () => ReactNode = () => {
        switch (true) {
            case props.isLoading:
                return (
                    <Grid.Item className="hist-row__value">
                        <div>
                            <Skeleton height="20px" variant="rectangle" />
                        </div>
                    </Grid.Item>
                )
            // error case, or done loading but no data
            case props.isError || (!props.isLoading && !props.tile):
                return (
                    <Grid.Item className="hist-row__value">
                        <Button
                            isIconOnly
                            onClick={() => {
                                logClick('retry operations detail load')
                                props.refetch()
                            }}
                            size="sm"
                            aria-label={t(generalErrors.loadFailedRetry)}
                            variant="ghost"
                        >
                            <Icon
                                svg={ErrorIcon}
                                className="hc8-clr-warning"
                                aria-hidden
                            />
                        </Button>
                    </Grid.Item>
                )
            default:
                return (
                    <Grid.Item className={`hist-row__value ${className}`}>
                        {props.metric_name === TOTAL_PRIORITY_FILL &&
                        SUCCESS_HEX.has(props.tile?.color || '')
                            ? null
                            : props.tile?.value}
                        {icon && (
                            <Icon
                                svg={icon as React.FunctionComponent<null>}
                                aria-hidden
                            />
                        )}
                    </Grid.Item>
                )
        }
    }

    return (
        <>
            <Grid.Container
                data-testid={`hist-line-item-${props.id}`}
                className="hist-row"
            >
                <Grid.Item className="hist-row__title" title={props.title}>
                    {props.title}
                </Grid.Item>
                {render()}
            </Grid.Container>
        </>
    )
}
